import { fillerColDef } from "@/lib/fillerColDef";
import CouponRateCellRenderer from "./cellRenderers/CouponRateCellRenderer";
import { couponRateValueFormatter } from "./cellRenderers/CouponRateCellRenderer/couponRateFormatter";
import {
  isPriceNotation,
  returnDecimalFormatting,
  returnFormattedDateSafe,
} from "@/lib/globalFormatterHelper";

import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { ColDef, MenuItemDef, SideBarDef } from "ag-grid-community";
import { cubeCustomColors } from "../../../../../tailwind.config";
import useTableUIStore from "@/store/tableUIStore";
import {
  defaultConfigTab,
  IactivePricesConfigIdNumberStoreArr,
} from "@/store/pricesTabStore";
import { ReferenceDataStatus } from "@/lib/interfaces and Enums/ENUMReferenceDataStatus";
import { IPriceProviderRow } from "@/lib/interfaces and Enums/InterfacePriceSummary";
import {
  clearAllFiltersContextMenuObject,
  collapseAllExpandedRowsContextMenuObject,
} from "@/components/widgets/tradeActionSummary/contextMenusRightClick/genericContextMenuItems";

// Shared by every filterable price column - the six numeric columns only differ by
// field, header and colId.
const PRICE_FILTER_PARAMS = {
  applyMiniFilterWhileTyping: true,
  buttons: ["reset"],
  closeOnApply: true,
};

// LQC-2575 - bond reference detail. The per-provider dropdown sits directly under its
// bond row, so it repeats none of these.
export const BOND_REFERENCE_ONLY_FIELDS = [
  "issuer",
  "currency",
  "maturityDate",
  "couponRate",
  "notation",
  "priceDate",
];

// Notation is a future task, so values stay plain decimals for now (LQC-2575).
export function priceValueFormatter(params: { value: any }) {
  return params.value
    ? returnDecimalFormatting(Number(params.value)).toString()
    : "";
}

// $p and $fr are both quoted as a price, so the column names the convention rather
// than the notation code. % is left as it is (LQC-2575).
export function notationValueGetter(params: { data?: { notation?: any } }) {
  const notation = params.data?.notation;
  if (!notation) return "";
  return isPriceNotation(String(notation)) ? "price" : String(notation);
}

// LQC-2909 - a config row is 2.75x its font size, so em keeps the reserve in step
// with the font-size setting. Three rows is the most worth holding back.
const CONFIG_ROW_HEIGHT_EM = 2.75;
const MAX_CONFIG_ROWS_RESERVED = 3;

// The floor the config list keeps when the widget is too short for everything: the
// help text below it gives way first, rather than the configs collapsing.
export function returnConfigListMinHeight(configCount: number): string {
  const rows = Math.min(Math.max(configCount, 0), MAX_CONFIG_ROWS_RESERVED);
  return `${rows * CONFIG_ROW_HEIGHT_EM}em`;
}

export function dateValueFormatter(params: { value: any }) {
  return returnFormattedDateSafe(params.value);
}

// Keep the group key as the raw ISO date. Without this AG Grid keys the group on the
// FORMATTED value and then re-runs the formatter over it, which cannot parse its own
// output (LQC-2575). It also keeps groups in date order rather than alphabetical.
export function dateKeyCreator(params: { value: any }) {
  return params.value ? String(params.value) : "";
}

interface INumericPriceColDef {
  field: string;
  headerName: string;
  name: string; // Feeds headerClass / colId, which saved configs are keyed on.
}

// Grouping by a price is meaningless, so these are value columns instead: the user
// drags one into the values panel and picks an aggregation.
function returnNumericPriceColDef({
  field,
  headerName,
  name,
}: INumericPriceColDef): ColDef {
  return {
    field,
    headerName,
    headerClass: `PriceTable${name}Header`,
    colId: `PriceTable${name}Cell`,
    filter: "agNumberColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: false,
    enableValue: true,
    width: 130,
    valueFormatter: priceValueFormatter,
    cellClass: "ag-right-aligned-cell",
  };
}

// The six numeric columns, shared by the master grid and the per-provider dropdown.
// Fields stay bid / mid / ask - only the header names them as yields (LQC-2575).
// Order is Bid, Ask, Mid so the two sides sit together and the derived mid follows.
export const PRICE_AND_YIELD_COLUMNS: INumericPriceColDef[] = [
  {
    field: "bidPrice",
    headerName: TableHeaderField.bidPrice,
    name: "BidPrice",
  },
  {
    field: "askPrice",
    headerName: TableHeaderField.askPrice,
    name: "AskPrice",
  },
  {
    field: "midPrice",
    headerName: TableHeaderField.midPrice,
    name: "MidPrice",
  },
  { field: "bid", headerName: TableHeaderField.bidYield, name: "Bid" },
  { field: "ask", headerName: TableHeaderField.askYield, name: "Ask" },
  { field: "mid", headerName: TableHeaderField.midYield, name: "Mid" },
];

export const priceTableColDefs: ColDef[] = [
  {
    field: "firstColumn",
    headerName: "",
    // Hook for the CSS that exempts this column from the font-size header floors,
    // which are wider than it is and would push the headers beside it out of line.
    headerClass: "PriceTableExpandHeader",
    // LQC-2575 - only ever holds the expand chevron, so it needs the width of the
    // icon plus its hit area, not a data column's. fixedWidth keeps the font-size
    // scaling off a control column (see applyDefaultMinWidths); minWidth is the
    // clickable floor the comment on the test below has always asked for.
    width: 48,
    minWidth: 48,
    fixedWidth: true,
    // pinned: "left",
    lockPosition: true, // Ensure it stays in the first position
    lockVisible: true,
    suppressMovable: true,
    resizable: false, // Prevents resizing
    sortable: false,
    suppressColumnsToolPanel: true,
    cellRenderer: "agGroupCellRenderer",
    suppressSizeToFit: true, // Prevents being included in auto-size
    // fixedWidth is read by the font-size scaling effect, not by AG Grid, so the
    // cast is needed to carry it on a ColDef.
  } as ColDef,
  {
    field: "isin",
    headerName: TableHeaderField.isin,
    headerClass: "PriceTableISINHeader",
    colId: "PriceTableISINCell",
    filter: "agTextColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 140,
  },
  {
    field: "securityName",
    headerName: TableHeaderField.securityName,
    headerClass: "PriceTableSecurityNameHeader",
    colId: "PriceTableSecurityNameCell",
    filter: "agTextColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 250,
  },
  {
    // LQC-2909 - the rate out of the security name as its own numeric column, so it
    // sorts and filters on value (> 4) rather than only "contains".
    field: "couponRate",
    headerName: TableHeaderField.couponRate,
    headerClass: "PriceTableCouponRateHeader",
    colId: "PriceTableCouponRateCell",
    filter: "agNumberColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 90,
    cellRenderer: CouponRateCellRenderer,
    valueFormatter: couponRateValueFormatter,
  },
  {
    field: "issuer",
    headerName: TableHeaderField.issuer,
    headerClass: "PriceTableIssuerHeader",
    colId: "PriceTableIssuerCell",
    // LQC-3117 - a checkbox list, so several can be picked at once.
    filter: "agSetColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 110,
  },
  {
    field: "currency",
    headerName: TableHeaderField.currency,
    headerClass: "PriceTableCurrencyHeader",
    colId: "PriceTableCurrencyCell",
    // LQC-3117 - a checkbox list, so several can be picked at once.
    filter: "agSetColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 110,
  },
  {
    field: "maturityDate",
    headerName: TableHeaderField.maturityDate,
    headerClass: "PriceTableMaturityDateHeader",
    colId: "PriceTableMaturityDateCell",
    cellDataType: "dateString",
    filter: "agDateColumnFilter",
    enableRowGroup: true,
    keyCreator: dateKeyCreator,
    width: 130,
    valueFormatter: dateValueFormatter,
  },
  {
    field: "notation",
    headerName: TableHeaderField.notation,
    headerClass: "PriceTableNotationHeader",
    colId: "PriceTableNotationCell",
    headerTooltip: "The convention this bond is quoted in",
    // A getter, not a formatter, so filtering, grouping and copy all see "Price" too.
    valueGetter: notationValueGetter,
    filter: "agTextColumnFilter",
    filterParams: PRICE_FILTER_PARAMS,
    enableRowGroup: true,
    width: 100,
    // LQC-2909 - off by default, still available in the columns tool panel.
    hide: true,
  },
  ...PRICE_AND_YIELD_COLUMNS.map((column) => returnNumericPriceColDef(column)),
  {
    field: "priceDate",
    headerName: TableHeaderField.priceDate,
    headerClass: "PriceTablePriceDateHeader",
    colId: "PriceTablePriceDateCell",
    headerTooltip: "The date these prices were published",
    cellDataType: "dateString",
    filter: "agDateColumnFilter",
    enableRowGroup: true,
    keyCreator: dateKeyCreator,
    width: 120,
    valueFormatter: dateValueFormatter,
  },
  ...fillerColDef,
];

// LQC-2575 - the dropdown grid is a static per-bond list, so it carries none of the
// master grid's filtering, grouping or reordering.
function returnDetailPriceColDef(column: INumericPriceColDef): ColDef {
  return {
    ...returnNumericPriceColDef(column),
    colId: `DetailPriceTable${column.name}Cell`,
    filter: false,
    enableRowGroup: false,
    enableValue: false,
    suppressMovable: true,
  };
}

// The dropdown sits directly under its bond row, so it repeats none of that row's
// reference detail (ISIN, security, issuer, currency, maturity, coupon, notation) - only
// is quoting and what they quote. priceDate stays: it is per provider, not per bond.
export function returnPriceDetailCols(): ColDef[] {
  const providerCol: ColDef = {
    field: "customerCode",
    headerName: "Provider",
    tooltipField: "customerName",
    width: 90,
    resizable: false,
    suppressMovable: true,
    lockPosition: true, // This ensures the column stays in the first position
    lockVisible: true,
    suppressSizeToFit: true,
    colId: "DetailPriceTableCustomerCode",
  };

  const priceDateCol: ColDef = {
    field: "priceDate",
    headerName: TableHeaderField.priceDate,
    headerClass: "priceTablePriceDateHeader",
    colId: "DetailPriceTablePriceDateCell",
    headerTooltip: "The date this provider published",
    suppressMovable: true,
    width: 120,
    cellDataType: "dateString",
    valueFormatter: dateValueFormatter,
  };

  return [
    providerCol,
    ...PRICE_AND_YIELD_COLUMNS.map((column) => returnDetailPriceColDef(column)),
    priceDateCol,
    ...fillerColDef,
  ];
}

// LQC-2575 - placeholder providers so the dropdown shows WHO is expected to quote
// before any provider prices exist. Every price cell is deliberately blank. Delete
// both of these once the provider-price endpoint lands.
export const PLACEHOLDER_PRICE_PROVIDERS = [
  "ANZ",
  "BAM",
  "BAR",
  "BMO",
  "BNP",
  "CA",
  "CBA",
  "CIT",
  "DB",
  "GS",
  "HSBC",
  "JPM",
  "MS",
  "NAB",
  "NOM",
  "NWM",
  "RBC",
  "SAN",
  "SOC",
  "TD",
  "UBS",
  "WBC",
];

export function returnPlaceholderProviderRows(): IPriceProviderRow[] {
  return PLACEHOLDER_PRICE_PROVIDERS.map((customerCode) => ({ customerCode }));
}

// LQC-2575 - "No rows to show" must only ever mean the fetch came back empty. Idle
// counts as loading: PriceIndex asks for the bonds on mount, so Idle is the tick
// before the request goes out.
export function isPriceReferenceDataLoading(
  status: ReferenceDataStatus,
): boolean {
  return (
    status == ReferenceDataStatus.Idle || status == ReferenceDataStatus.Loading
  );
}

// LQC-2575 - one entry per Prices widget, keyed on its cryptoID. Every other widget's
// entry is preserved: the previous version replaced them with this widget's, so two
// widgets ended up holding duplicates of whichever mounted last.
export function returnActiveConfigIdArrForWidget(
  current: IactivePricesConfigIdNumberStoreArr[],
  cryptoID: string | undefined,
  activeTabId: number,
): IactivePricesConfigIdNumberStoreArr[] {
  const otherWidgetsEntries = current.filter(
    (config) => config.crypto != cryptoID,
  );
  return [...otherWidgetsEntries, { crypto: cryptoID, activeTabId }];
}

// LQC-2575 - a config is a named, SHARED view: its columns AND its filter are what
// make "UST" mean UST. Default is the unnamed scratch sheet, so its filter is never
// saved: throwaway, this session and this widget only.
export function shouldPersistFiltersToConfig(configId: number): boolean {
  return configId != defaultConfigTab.id;
}

// The filler column is flex, so its width is whatever the widget is wide. Saving it
// makes two widgets on one config disagree on every write and push state at each
// other forever, so it is dropped - it is locked right and always visible anyway.
export function returnColumnStateToSave<T extends { colId?: string }>(
  columnState: T[],
): T[] {
  return columnState.filter((column) => column.colId != "filler");
}

// LQC-2575 - the row grouping slice of a saved column state. Applying only these
// three properties syncs grouping between widgets on one config without dragging
// widths and order across with it, which would make columns twitch mid-resize.
// Every column is listed, so ungrouping in one widget clears it in the others.
interface IRowGroupColumnState {
  colId: string;
  rowGroup: boolean;
  rowGroupIndex: number | null;
}

export function returnRowGroupStateToApply<
  T extends {
    colId?: string;
    rowGroup?: boolean | null;
    rowGroupIndex?: number | null;
  },
>(columnState: T[]): IRowGroupColumnState[] {
  return columnState.flatMap((column) =>
    column.colId
      ? [
          {
            colId: column.colId,
            rowGroup: column.rowGroup ?? false,
            rowGroupIndex: column.rowGroupIndex ?? null,
          },
        ]
      : [],
  );
}

// What gets written into a config's configJson.
export function returnConfigJsonForGrid(
  columnState: unknown,
  filterModel: unknown,
  configId: number,
): string {
  return JSON.stringify(
    shouldPersistFiltersToConfig(configId)
      ? { columnState, filterModel }
      : { columnState },
  );
}

// Sidebar for the prices table
export function returnPriceTableSidebarObj(
  activePricesConfigIdNumber: number,
  setActivePricesConfigIdNumber: Function,
  cryptoID?: string | undefined,
): SideBarDef {
  const priceTableSidebarReturnObj: SideBarDef = {
    toolPanels: [
      {
        id: "configs",
        labelDefault: "Views",
        labelKey: "",
        // Registered via PriceTable's `icons` prop.
        iconKey: "views",
        toolPanel: "customConfigsToolPanel",
        toolPanelParams: {
          activePricesConfigIdNumber,
          setActivePricesConfigIdNumber,
          cryptoID,
        },
        // LQC-2575 - a matched min and max is what removes AG Grid's resize handle,
        // so this panel could not be widened at all. It opens wide enough for the
        // two buttons to sit on one row, and the config names stretch into any
        // extra width the user drags out.
        minWidth: 225,
        maxWidth: 500,
        width: 275,
      },
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        minWidth: 225,
        maxWidth: 225,
        width: 225,
        // LQC-2575 - row groups and values stay available so a user can group by
        // issuer / currency and aggregate the price columns.
        toolPanelParams: {
          suppressPivotMode: true,
        },
      },
      {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
        minWidth: 180,
        maxWidth: 400,
        width: 250,
      },
    ],
    position: "left",
    // defaultToolPanel: "configs",
  };
  return priceTableSidebarReturnObj;
}

// The detail grid runs domLayout="autoHeight" (as the blotter's nested tables do), so
// AG Grid sizes it to its rows plus the horizontal scrollbar. This floor only stops a
// one-provider dropdown looking like a glitch.
export const PRICE_DETAIL_MIN_HEIGHT = 150;

// CSS STYLING to the Detail table.

export function mainPriceGridStyles(
  theme: string | undefined,
  tableStripeOddTrue: boolean,
) {
  // Get colors from the store
  const pricesColorLight = useTableUIStore.getState().pricesColorLight;
  const pricesColorDark = useTableUIStore.getState().pricesColorDark;
  const pricesFlashColorLight =
    useTableUIStore.getState().pricesFlashColorLight;
  const pricesFlashColorDark = useTableUIStore.getState().pricesFlashColorDark;

  // Helper function to adjust color brightness
  const adjustBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  if (theme == "light") {
    const baseColor = pricesColorLight;
    const stripeColor = tableStripeOddTrue
      ? adjustBrightness(baseColor, -5)
      : baseColor;
    const hoverColor = adjustBrightness(baseColor, -10);

    return {
      "--ag-background-color": baseColor,
      "--ag-odd-row-background-color": stripeColor,
      "--ag-row-hover-color": hoverColor,
      "--ag-value-change-value-highlight-background-color":
        pricesFlashColorLight,
    };
  } else {
    // DARK
    const baseColor = pricesColorDark;
    const stripeColor = tableStripeOddTrue
      ? adjustBrightness(baseColor, 5)
      : baseColor;
    const hoverColor = adjustBrightness(baseColor, 10);

    return {
      "--ag-background-color": baseColor,
      "--ag-odd-row-background-color": stripeColor,
      "--ag-row-hover-color": hoverColor,
      "--ag-value-change-value-highlight-background-color":
        pricesFlashColorDark,
    };
  }
}

export function priceDetailGridStyles(
  theme: string | undefined,
  tableStripeOddTrue: boolean,
) {
  // Hover is deliberately NOT set here: an inline value beats the stylesheet, and
  // .cssStylingOnNestedTable1 in globals.css already carries the blotter's hover,
  // derived from the detail row background rather than a bright blue.
  if (theme == "light") {
    return {
      "--ag-background-color": "transparent",
      "--ag-odd-row-background-color": tableStripeOddTrue
        ? cubeCustomColors.cubeTableRowColour[300]
        : "transparent",
    };
  } else {
    // DARK
    return {
      "--ag-background-color": "transparent",
      "--ag-odd-row-background-color": tableStripeOddTrue
        ? cubeCustomColors.cubeTableRowColour[500]
        : "transparent",
    };
  }
}

// custom getMainMenuItems for price table to prevent non resizeable cols from getting impacted by the autosize.
export function retunGetMainMenuItems(params: any) {
  const defaultItems = params.defaultItems;

  // Find the 'autoSizeAll' menu item and customize its action
  const customizedItems = defaultItems.map((item: any) => {
    if (item === "autoSizeAll") {
      return {
        name: "Auto-Size All Columns", // Optional: You can rename it
        action: () => {
          // Get all the columns
          const allColumns = params.api.getColumns();

          // Filter out columns that should not be resized
          if (allColumns) {
            const resizableColumns = allColumns.filter(
              (col: any) => col.getColDef().resizable !== false,
            );

            // Auto-size only the resizable columns
            params.api.autoSizeColumns(resizableColumns);
          }
        },
      };
    }
    return item;
  });

  return customizedItems;
}

// Context menu for price table
export function getContextMenuItemsPriceTable() {
  const result: (string | MenuItemDef)[] = [
    "copy",
    "copyWithHeaders",
    "separator",
    "export",
    "separator",
    { ...clearAllFiltersContextMenuObject },
    { ...collapseAllExpandedRowsContextMenuObject },
  ];

  return result;
}
